import { type RefObject, useEffect } from 'react';

import { usePreservedCallback } from './usePreservedCallback';

//  이벤트 리스너를 연결할 수 있는 요소를 나타냅니다.
export type EventListenerAvailableElement = Window | Document | HTMLElement | SVGElement | MediaQueryList;
export type TargetElement<T extends EventListenerAvailableElement> = T | RefObject<T | null | undefined>;

const isRefObject = <T extends EventListenerAvailableElement>(
  element: TargetElement<T> | null
): element is RefObject<T | null | undefined> => {
  if (!element) return false;
  return Object.prototype.hasOwnProperty.call(element, 'current');
};

/**
 * @description Window 객체에 이벤트 리스너를 추가하고, 컴포넌트가 언마운트될 때 자동으로 제거합니다.
 */
export function useEventListener<K extends keyof WindowEventMap>(
  element: Window | null,
  type: K,
  listener: (event: WindowEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): void;
/**
 * @description Document 객체에 이벤트 리스너를 추가하고, 컴포넌트가 언마운트될 때 자동으로 제거합니다.
 */
export function useEventListener<K extends keyof DocumentEventMap>(
  element: Document | null,
  type: K,
  listener: (event: DocumentEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): void;
/**
 * @description MediaQueryList 객체에 이벤트 리스너를 추가하고, 컴포넌트가 언마운트될 때 자동으로 제거합니다.
 */
export function useEventListener<K extends keyof MediaQueryListEventMap>(
  element: MediaQueryList | null,
  type: K,
  listener: (event: MediaQueryListEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): void;
/**
 * @description 지정된 HTML 요소에 이벤트 리스너를 추가하고, 컴포넌트가 언마운트될 때 자동으로 제거합니다.
 */
export function useEventListener<K extends keyof HTMLElementEventMap, T extends HTMLElement>(
  element: TargetElement<T> | null,
  type: K,
  listener: (event: HTMLElementEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): void;
/**
 * @description 지정된 SVG 요소에 이벤트 리스너를 추가하고, 컴포넌트가 언마운트될 때 자동으로 제거합니다.
 */
export function useEventListener<K extends keyof SVGElementEventMap, T extends SVGElement>(
  element: TargetElement<T> | null,
  type: K,
  listener: (event: SVGElementEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): void;
export function useEventListener<
  W extends keyof WindowEventMap,
  D extends keyof DocumentEventMap,
  M extends keyof MediaQueryListEventMap,
  E extends keyof HTMLElementEventMap,
  S extends keyof SVGElementEventMap,
  T extends EventListenerAvailableElement,
>(
  element: TargetElement<T> | null,
  type: W | D | M | E | S,
  listener: (
    event:
      | WindowEventMap[W]
      | DocumentEventMap[D]
      | HTMLElementEventMap[E]
      | SVGElementEventMap[S]
      | MediaQueryListEventMap[M]
      | Event
  ) => void,
  options?: boolean | AddEventListenerOptions
): void {
  const preservedListener = usePreservedCallback(listener);

  useEffect(() => {
    const targetElement = isRefObject(element) ? element.current : element;

    if (!targetElement) return;

    // event registration
    targetElement.addEventListener(type, preservedListener, options);

    // clean up
    return () => {
      targetElement.removeEventListener(type, preservedListener, options);
    };
  }, [type, element, options, preservedListener]);
}
